import { Link, useParams } from "react-router";
import { Alerta, Badge, Button, Card, DataList, Dialog, Row, Spinner, Stack } from "@/components";
import type { LineaLlamado } from "@/lib/api";
import { ETIQUETA_ESTADO_LLAMADO, TONO_ESTADO_LLAMADO } from "@/lib/aportes";
import { formatFecha, formatMoneda } from "@/lib/format";
import { formatParticipacion } from "@/lib/participacion";
import { FormularioLlamado } from "./FormularioLlamado";
import { useLlamadoTab } from "./useLlamadoTab";

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="contents">
      <dt className="text-chico text-texto-suave">{etiqueta}</dt>
      <dd className="font-medium">{valor}</dd>
    </div>
  );
}

/** `key={id}` fuerza un remount al cambiar de llamado (mismo criterio que GastoTab). */
export function LlamadoTab() {
  const { id } = useParams();
  return <Ficha key={id} />;
}

function Ficha() {
  const { id } = useParams();
  const t = useLlamadoTab(Number(id));
  const l = t.llamado;

  const columnas = [
    { clave: "nombre", titulo: "Fiduciante", render: (x: LineaLlamado) => x.nombre },
    { clave: "clase", titulo: "Clase", render: (x: LineaLlamado) => <Badge tono={x.clase === "B" ? "alerta" : "neutro"}>{x.clase}</Badge> },
    { clave: "porcentaje", titulo: "Participación", render: (x: LineaLlamado) => formatParticipacion(x.porcentaje) },
    { clave: "pedido", titulo: "Pedido", render: (x: LineaLlamado) => formatMoneda(x.pedido_usd, "USD") },
    { clave: "imputado", titulo: "Aportado", render: (x: LineaLlamado) => formatMoneda(x.imputado_usd, "USD") },
    {
      clave: "pendiente",
      titulo: "Pendiente",
      render: (x: LineaLlamado) => (
        <Row espacio={2}>
          <span>{formatMoneda(x.pendiente_usd, "USD")}</span>
          {x.vencida && <Badge tono="peligro">Vencido</Badge>}
        </Row>
      ),
    },
  ];

  return (
    <Stack espacio={6}>
      <Link to="/llamados" className="text-chico text-primario">
        ← Previsiones de gastos
      </Link>
      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      <Alerta texto={t.errorCarga} />
      {l && (
        <>
          <Row justificar="entre" envolver espacio={3}>
            <Stack espacio={1}>
              <Row espacio={2}>
                <h1 className="text-pantalla font-light tracking-[-0.03em]">Previsión {l.numero}</h1>
                <Badge tono={TONO_ESTADO_LLAMADO[l.estado]}>{ETIQUETA_ESTADO_LLAMADO[l.estado]}</Badge>
                {l.montos_a_mano && <Badge tono="primario">Montos redondos</Badge>}
              </Row>
              {l.descripcion && <p className="text-texto-suave">{l.descripcion}</p>}
            </Stack>
            {t.esAdmin && (
              <Row espacio={2}>
                <Button variante="secundario" onClick={t.abrirEditar}>
                  Editar
                </Button>
                <Button variante="fantasma" onClick={t.abrirBorrar}>
                  Borrar
                </Button>
              </Row>
            )}
          </Row>
          <Card>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
              <Dato etiqueta="Fecha" valor={formatFecha(l.fecha)} />
              <Dato etiqueta="Vencimiento" valor={formatFecha(l.vencimiento)} />
              <Dato etiqueta="Monto" valor={formatMoneda(l.monto_usd, "USD")} />
              {l.cubierto_con_ventas_usd !== "0.00" && <Dato etiqueta="Cubierto con ventas" valor={formatMoneda(l.cubierto_con_ventas_usd, "USD")} />}
              <Dato etiqueta="Aportado" valor={formatMoneda(l.aportado_usd, "USD")} />
              <Dato etiqueta="Pendiente" valor={formatMoneda(l.pendiente_usd, "USD")} />
            </dl>
          </Card>
          <DataList etiqueta="Reparto de la previsión" columnas={columnas} filas={l.lineas} claveFila={(x) => `${x.fiduciante_id}-${x.clase}`} vacio="Tu ficha todavía no está vinculada a esta previsión." />
        </>
      )}

      <Dialog abierto={t.dialogo === "editar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Editar previsión">
        <FormularioLlamado
          form={t.form}
          cambiar={t.cambiar}
          errores={t.errores}
          vistaPrevia={t.vistaPrevia}
          errorVistaPrevia={t.errorVistaPrevia}
          onSubmit={(e) => void t.guardar(e)}
          onCancelar={t.cerrar}
          guardando={t.guardando}
          error={t.errorGuardar}
          textoGuardar="Guardar"
          montosAMano={l?.montos_a_mano ?? false}
        />
      </Dialog>

      <Dialog abierto={t.dialogo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar previsión">
        <Stack espacio={4}>
          <p>¿Borrar la previsión {l?.numero}? Los aportes no se pierden: se aplican a la siguiente previsión o quedan a favor. No se puede deshacer.</p>
          <Alerta texto={t.errorBorrar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cerrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
              Borrar
            </Button>
          </Row>
        </Stack>
      </Dialog>
    </Stack>
  );
}
